import {
  GetQBOFileResponse,
  ImportDefaultCategory,
  UploadQBOFileResponse,
} from "types/Models/books";
import { emptyApi } from "./emptyApi";
import { makeFormData } from "utils/makeFormData";

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    uploadQBOfile: build.mutation<
      UploadQBOFileResponse,
      {
        entityId: string;
        payload: {
          coa_file: File;
          vendor_file: File;
          date_till_import: string;
          customer_file: File;
          je_file: File;
          prev_fy_end_date: string;
          balance_sheet_file: File;
          invoice_file: File;
          bill_file: File;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        const form = makeFormData(payload);

        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_import/`,
          method: "POST",
          body: form,
        };
      },
      invalidatesTags: ["CHART_OF_ACCOUNTS"],
    }),
    finalizeQBOImport: build.mutation<
      void,
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_import/`,
          method: "PATCH",
        };
      },
      invalidatesTags: ["CHART_OF_ACCOUNTS"],
    }),
    getFileUploadQBOfile: build.query<
      GetQBOFileResponse,
      {
        entityId: string;
      }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_import/`,
          method: "GET",
        };
      },
      providesTags: ["CHART_OF_ACCOUNTS"],
    }),
    getQBOImportDefaultCategories: build.query<
      ImportDefaultCategory[],
      { entityId: string }
    >({
      query: ({ entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_import/default_category_mapping/`,
        };
      },
    }),
    updateQBOImportDefaultCategories: build.mutation<
      ImportDefaultCategory[],
      {
        entityId: string;
        defaultCategoryMapping: Record<string, number>;
      }
    >({
      query: ({ entityId, defaultCategoryMapping }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/bookkeeping_import/default_category_mapping/`,
          method: "PUT",
          body: { default_category_mapping: defaultCategoryMapping },
        };
      },
      async onQueryStarted(
        { entityId, defaultCategoryMapping },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            extendedApi.util.updateQueryData(
              "getQBOImportDefaultCategories",
              { entityId },
              () => data
            )
          );
          dispatch(
            extendedApi.util.updateQueryData(
              "getFileUploadQBOfile",
              { entityId },
              (draft) => {
                draft.default_category_mapping = {
                  ...draft.default_category_mapping,
                  ...defaultCategoryMapping,
                };
              }
            )
          );
        } catch {
          // The caller surfaces the error through unwrap()
        }
      },
    }),
  }),
});
export const {
  useUploadQBOfileMutation,
  useFinalizeQBOImportMutation,
  useGetFileUploadQBOfileQuery,
  useGetQBOImportDefaultCategoriesQuery,
  useUpdateQBOImportDefaultCategoriesMutation,
} = extendedApi;
